import type { KeyboardEvent } from "react";
import { useRef } from "react";
import IconCheck from "ui/icons/IconCheck";
import IconChevron from "ui/icons/IconChevron";
import Popover from "ui/popovers/Popover";
import styles from "./ColumnHeaderMenu.module.scss";

interface ColumnHeaderMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnLabel?: string;
  canSort: boolean;
  sortDirection: false | "asc" | "desc";
  onSortAsc: () => void;
  onSortDesc: () => void;
  onClearSort: () => void;
  canMaximize: boolean;
  onMaximizeColumn: () => void;
  /** Only provided in horizontal-overflow mode, where every column can grow at once. */
  onMaximizeAllColumns?: () => void;
}

/**
 * Hover-reveal caret in a column header. Left-clicking opens a Popover of column controls
 * (sorting + width). Anchored to the trigger so it escapes the table's overflow clipping and
 * inherits dismissal (outside click / escape).
 */
export default function ColumnHeaderMenu({
  open,
  onOpenChange,
  columnLabel,
  canSort,
  sortDirection,
  onSortAsc,
  onSortDesc,
  onClearSort,
  canMaximize,
  onMaximizeColumn,
  onMaximizeAllColumns,
}: ColumnHeaderMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  const runAndClose = (fn: () => void) => () => {
    fn();
    onOpenChange(false);
  };

  // Roving focus across the menuitem buttons; Radix's Popover already handles Escape and outside-click.
  const onMenuKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") {
      return;
    }
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? []);
    if (items.length === 0) {
      return;
    }
    e.preventDefault();
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement);
    // When focus isn't yet on an item (-1), ArrowDown starts at the first and ArrowUp at the last.
    let nextIndex: number;
    if (currentIndex === -1) {
      nextIndex = e.key === "ArrowDown" ? 0 : items.length - 1;
    } else {
      const delta = e.key === "ArrowDown" ? 1 : -1;
      nextIndex = (currentIndex + delta + items.length) % items.length;
    }
    items[nextIndex].focus();
  };

  return (
    <Popover.Root open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.trigger}
          aria-label={columnLabel ? `${columnLabel} column options` : "Column options"}
          data-testid="column-header-menu-trigger"
          // Stop the header's mousedown sort toggle from firing when interacting with the caret.
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <IconChevron rotate={90} size={9} />
        </button>
      </Popover.Trigger>
      <Popover.Content side="bottom" align="end" sideOffset={4}>
        {/* The popover is portaled to the body, but React events still bubble through the React
            tree to the header cell's mousedown sort handler; stop them here so menu clicks don't sort. */}
        <div
          ref={menuRef}
          className={styles.menu}
          role="menu"
          onKeyDown={onMenuKeyDown}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {canSort && (
            <div className={styles.group} role="group">
              <MenuItem
                label="Sort ascending"
                testId="sort-asc"
                checked={sortDirection === "asc"}
                onSelect={runAndClose(onSortAsc)}
              />
              <MenuItem
                label="Sort descending"
                testId="sort-desc"
                checked={sortDirection === "desc"}
                onSelect={runAndClose(onSortDesc)}
              />
              {sortDirection && <MenuItem label="Clear sort" testId="clear-sort" onSelect={runAndClose(onClearSort)} />}
            </div>
          )}
          {canMaximize && (
            <div className={styles.group} role="group">
              <MenuItem label="Maximize column" testId="maximize-column" onSelect={runAndClose(onMaximizeColumn)} />
              {onMaximizeAllColumns && (
                <MenuItem
                  label="Maximize all columns"
                  testId="maximize-all-columns"
                  onSelect={runAndClose(onMaximizeAllColumns)}
                />
              )}
            </div>
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

function MenuItem({
  label,
  testId,
  onSelect,
  checked,
}: {
  label: string;
  testId: string;
  onSelect: () => void;
  checked?: boolean;
}) {
  return (
    <button type="button" role="menuitem" className={styles.item} onClick={onSelect} data-testid={testId}>
      <span className={styles.itemIcon}>{checked && <IconCheck />}</span>
      {label}
    </button>
  );
}
